import { useEffect, useState } from "react";
import { Box, Typography, TextField, Button } from "@mui/material";
import "../css/InfoCard.css";
import { useTranslation } from "react-i18next";

/**
 * Editable list of strings (e.g. education / certifications),
 * styled exactly like InfoCard (same CSS classes and buttons).
 *
 * Props:
 *  - title:    already-translated card title
 *  - items:    string[]
 *  - onSave:   async (cleanItems: string[]) => void   (should throw on failure)
 *  - editable: show the edit button (default true)
 */
export default function StringListCard({
  title,
  items = [],
  onSave,
  editable = true,
}) {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(items);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // keep the card in sync when fresh data arrives (e.g. after a save)
  useEffect(() => {
    if (!isEditing) setDraft(items);
  }, [items, isEditing]);

  const handleEditClick = () => {
    setDraft(items.length ? items : [""]);
    setError(null);
    setIsEditing(true);
  };

  const handleCancelClick = () => {
    setDraft(items);
    setError(null);
    setIsEditing(false);
  };

  const updateItem = (index, value) =>
    setDraft((prev) => prev.map((v, i) => (i === index ? value : v)));

  const removeItem = (index) =>
    setDraft((prev) => prev.filter((_, i) => i !== index));

  const addItem = () => setDraft((prev) => [...prev, ""]);

  const handleSaveClick = async () => {
    const clean = draft.map((v) => v.trim()).filter(Boolean);
    setLoading(true);
    setError(null);
    try {
      await onSave(clean);
      setIsEditing(false);
    } catch (err) {
      console.error("Save list error:", err);
      setError(t("staff_lists.saveError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className="info-card">
      <Box className="info-card-header">
        <Typography variant="header">{title}</Typography>
        {!isEditing && editable && (
          <Button
            onClick={handleEditClick}
            className="info-card-edit-btn"
            disabled={loading}
          >
            {t("actions.edit")}
          </Button>
        )}
        {isEditing && (
          <Box style={{ display: "flex", gap: 8 }}>
            <Button
              onClick={handleSaveClick}
              className="info-card-save-btn"
              disabled={loading}
            >
              {t("actions.save")}
            </Button>
            <Button
              onClick={handleCancelClick}
              className="info-card-cancel-btn"
              disabled={loading}
            >
              {t("actions.cancel")}
            </Button>
          </Box>
        )}
      </Box>

      <Box className="info-card-content">
        {isEditing ? (
          <>
            {draft.map((value, i) => (
              <Box key={i} className="info-row">
                <Box className="info-label">{i + 1}</Box>
                <Box
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  <TextField
                    size="small"
                    fullWidth
                    value={value}
                    onChange={(e) => updateItem(i, e.target.value)}
                    placeholder={t("staff_lists.placeholder")}
                    className="info-text-field"
                    disabled={loading}
                    InputProps={{ style: { fontSize: 14 } }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    onClick={() => removeItem(i)}
                    disabled={loading}
                  >
                    {t("staff_lists.remove")}
                  </Button>
                </Box>
              </Box>
            ))}
            <Button
              onClick={addItem}
              disabled={loading}
              sx={{
                color: "#702E8F",
                width: "9rem",
                fontWeight: 600,
                fontSize: "medium",
                textTransform: "none",
              }}
            >
              + {t("staff_lists.add")}
            </Button>
          </>
        ) : items.length > 0 ? (
          items.map((item, i) => (
            <Box key={`${item}-${i}`} className="info-row">
              <Box className="info-label">{i + 1}</Box>
              <Box className="info-value">{item || "---"}</Box>
            </Box>
          ))
        ) : (
          <Box className="info-row">
            <Box className="info-value">---</Box>
          </Box>
        )}

        {error && (
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </Box>
    </Box>
  );
}